"use client";

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AuthContextType, LoginResponse, UserResponse, AuthStorage } from "@/types/auth";
import { clearAll as clearOfflineQueue } from "@/lib/offline-queue";
import { clearAll as clearChatOfflineCache } from "@/services/chatOfflineStore";
import { executarAntesDeSair } from "@/lib/chamadas/antesDeSair";
import { marcarDonoDoPush, soltarDonoDoPush } from "@/lib/push/donoDoPush";
import { queryClient } from "@/lib/queryClient";
import {
  ANTECEDENCIA_DA_RENOVACAO_MS,
  ESPERA_DEPOIS_DE_FALHA_MS,
} from "@/lib/sessao/prazosDaSessao";
import { aparelhoAtual, renovarSessao } from "@/lib/sessao/renovarSessao";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_STORAGE_KEY = "seweb_auth";

// API base URL - ajuste conforme necessário
const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api/v1";

// Mesmo Service Worker que o `usePushSubscription` registra.
const PUSH_SERVICE_WORKER_URL = "/service-worker.js";

/**
 * Passo 8 (Ruling R10): apaga NO SERVIDOR a inscrição de push deste navegador para quem está
 * saindo. Melhor-esforço: qualquer falha (sem service worker, sem inscrição, rede, 401, 404) é
 * engolida por quem chama. Usa `fetch` direto com o token capturado ANTES de o logout limpar o
 * storage (o `api` já não teria o token). NÃO chama `subscription.unsubscribe()`: a permissão e
 * a inscrição do navegador ficam, e quando o mesmo usuário voltar o `ReinscricaoDoPush` regrava
 * a linha dele. Sessão que vence sem logout, sem ninguém entrar depois, mantém a linha.
 */
async function apagarInscricaoDoPush(accessToken: string): Promise<void> {
  if (!accessToken) return;
  const sw = typeof navigator === "undefined" ? undefined : navigator.serviceWorker;
  const registro = await sw?.getRegistration(PUSH_SERVICE_WORKER_URL);
  const inscricao = await registro?.pushManager?.getSubscription();
  if (!inscricao) return;
  await fetch(`${API_BASE_URL}/push/subscribe`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ endpoint: inscricao.endpoint }),
  });
}

// Fora do componente de propósito: só falam com o localStorage, e assim os efeitos e o `logout` (estável) os usam sem listá-los como dependência.
function getStoredAuthData(): AuthStorage | null {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function setStoredAuthData(data: AuthStorage | null) {
  if (data) {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(data));
  } else {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    // A sessão interna acabou (o "Sair" e TODAS as saídas forçadas passam por aqui: refresh recusado, sem refresh token, sem rede): a
    // marca de dono do push do aparelho sai junto SE for do interno. Senão a linha do colaborador ficaria no servidor e o parceiro do
    // portal, no mesmo navegador, não tomaria o push — os avisos do colaborador seguiriam chegando ali (decisão de 12/10/2026).
    soltarDonoDoPush("interno");
  }
}

/** Maior espera que o `setTimeout` aceita (2^31 − 1 ms, ~24,8 dias); acima disso o navegador dispara na hora. */
const TETO_DO_TIMER_MS = 2_147_483_647;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true); // Começa como true para aguardar verificação
  const [error, setError] = useState<string | null>(null);

  // Até quando NÃO renovar sozinho: depois de um "tentar_depois" (abertura, timer, aba que voltou) a próxima tentativa é daqui a
  // `ESPERA_DEPOIS_DE_FALHA_MS`. Sem isso, o access já dentro dos 5 min de folga renovaria de novo na hora, martelando um servidor em manutenção.
  const esperarAte = useRef(0);

  // Verificar autenticação ao carregar
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const authData = getStoredAuthData();
        if (authData && authData.access_token) {
          // Verificar se o token ainda é válido
          if (Date.now() < authData.expires_at) {
            setUser(authData.user);
          } else {
            console.log("[AuthContext] Token expired, attempting refresh...");
            const resultado = await renovarSessao();
            if (resultado === "recusou") {
              // O servidor negou (401/403) ou a sessão já não existe: só isso prova o fim da sessão.
              console.log("[AuthContext] Token refresh refused, logging out");
              logout();
            } else {
              // "renovou": o par novo já está guardado. "tentar_depois" (manutenção, rede caída, resposta estranha): NÃO prova o fim da
              // sessão e a pessoa entra com o usuário guardado — a primeira requisição leva 401 e tenta renovar de novo; o efeito de
              // renovação também tenta, em `ESPERA_DEPOIS_DE_FALHA_MS`. Derrubar aqui mandava para o login quem só estava sem rede.
              if (resultado === "tentar_depois") {
                console.log("[AuthContext] Token refresh postponed, keeping the stored user");
                esperarAte.current = Date.now() + ESPERA_DEPOIS_DE_FALHA_MS;
              }
              setUser(getStoredAuthData()?.user ?? null);
            }
          }
        }
      } catch (error) {
        console.error("[AuthContext] Error checking auth:", error);
        // Em caso de erro, limpar dados de autenticação
        setStoredAuthData(null);
        setUser(null);
      } finally {
        setIsLoading(false); // Sempre finaliza o loading após verificação
      }
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        // O servidor dá prazos de sessão diferentes ao app instalado e ao navegador: quem diz qual é o front.
        body: JSON.stringify({ email, password, aparelho: aparelhoAtual() }),
      });

      if (!response.ok) {
        // Achado (sugestão, item 12): um 503 SEM corpo JSON (ex.: "no
        // available server" em texto puro — resposta típica do Traefik
        // quando o backend cai de vez, não uma manutenção controlada)
        // quebrava `response.json()` com "Unexpected token"; esse erro de
        // parsing cru chegava pro usuário em vez de um aviso legível.
        let errorData: { detail?: string; maintenance?: boolean; message?: string } = {};
        try {
          errorData = await response.json();
        } catch {
          throw new Error("Não foi possível conectar ao servidor. Tente novamente em instantes.");
        }

        // Manutenção (28/09/2026, contrato Front — docs/superpowers/plans/
        // 2026-09-28-manutencao-no-login.md): POST /auth/login virou o único
        // lugar que decide bloquear por manutenção (o middleware ficou
        // isento) — o 503 tem o MESMO formato dele (`maintenance`, `message`,
        // `level`, `estimated_end`, `retry_after`). Sem este caso, o 503
        // caía no `errorData.detail` (que ele não tem) e mostrava "Erro ao
        // fazer login" — indistinguível de qualquer outro erro genérico.
        if (response.status === 503 && errorData?.maintenance === true) {
          const erroDeManutencao = new Error(
            errorData.message || "O sistema está em manutenção. Tente novamente mais tarde."
          ) as Error & { manutencao?: boolean };
          erroDeManutencao.manutencao = true;
          throw erroDeManutencao;
        }

        throw new Error(errorData.detail || "Erro ao fazer login");
      }

      const loginData: LoginResponse = await response.json();

      // Calcular timestamp de expiração
      const expiresAt = Date.now() + loginData.tokens.expires_in * 1000;

      const authStorage: AuthStorage = {
        access_token: loginData.tokens.access_token,
        refresh_token: loginData.tokens.refresh_token,
        user: {
          user_id: loginData.user_id,
          email: loginData.email,
          first_name: loginData.first_name,
          last_name: loginData.last_name,
          is_active: true,
          last_login: new Date().toISOString(),
          perfil: loginData.perfil,
          is_external: loginData.is_external,
          substabelecido_id: loginData.substabelecido_id,
          password_change_required: loginData.password_change_required ?? false,
        },
        expires_at: expiresAt,
      };

      setStoredAuthData(authStorage);
      // Entrar é um dos dois gestos que trocam o dono do push do aparelho (o outro é ligar no cartão; decisão de 12/10/2026). A marca
      // vai junto com a sessão, antes do `setUser`: o `ReinscricaoDoPush` monta no render seguinte e consulta a marca.
      marcarDonoDoPush("interno");
      setUser(authStorage.user);

      // Achado BAIXO (revisão de segurança, 28/09/2026, item 9): a queryKey
      // ["maintenance-status"] não depende do usuário — num device
      // compartilhado, o podeOperar em cache (do usuário ANTERIOR) ficava
      // na tela até o próximo poll do MaintenanceProvider.
      queryClient.invalidateQueries({ queryKey: ["maintenance-status"] });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro desconhecido");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // A saída de verdade. `servidorJaEncerrou` é o "Sair de todos os aparelhos" depois do 204: o servidor já revogou o access e apagou o
  // push, então o que falaria com ele com esse access (o gancho da ligação e o DELETE do push) leva 401 e só suja o console. Tudo o que é
  // local continua igual: storage, estado, caches offline, dono do push e o `/auth/logout` (que não leva Authorization).
  // `useCallback` com deps vazias: só usa setters de estado e funções de módulo, e os efeitos de renovação o listam sem se religar a cada render.
  const encerrarSessaoLocal = useCallback((servidorJaEncerrou: boolean): void => {
    console.log("[AuthContext] Logging out user");

    // Get tokens before clearing storage
    const authAntes = getStoredAuthData();
    const refreshTokenValue = authAntes?.refresh_token || "";
    const accessTokenValue = authAntes?.access_token || "";

    // Antes de apagar o token: quem tem algo a encerrar no servidor (a ligação do chat em curso) o recebe
    // agora, porque o provider só desmonta depois e o pedido dele já sairia sem `Authorization` (401). Um
    // gancho que lança vai ao console e não impede o logout (R4).
    if (servidorJaEncerrou) executarAntesDeSair(accessTokenValue, { semRede: true });
    else executarAntesDeSair(accessTokenValue);

    // Clear local state and storage
    setStoredAuthData(null);
    setUser(null);
    setError(null);

    // Achado BAIXO (item 9): mesmo motivo do login — o próximo usuário a
    // entrar nesta aba não pode herdar o podeOperar em cache de quem saiu.
    queryClient.invalidateQueries({ queryKey: ["maintenance-status"] });

    // Story X1.3.3 — device compartilhado não pode vazar dados do usuário
    // anterior: limpa a fila de ações offline + o read-cache de chat.
    void clearOfflineQueue().catch((e: unknown) =>
      console.debug("[AuthContext] clearOfflineQueue falhou:", e)
    );
    void clearChatOfflineCache().catch((e: unknown) =>
      console.debug("[AuthContext] clearChatOfflineCache falhou:", e)
    );

    // Device compartilhado (mesmo motivo de cima): o push leva título e trecho do aviso, então a
    // inscrição deste navegador sai do nome de quem saiu. Melhor-esforço, sem esperar. No "Sair de todos"
    // o servidor já apagou as inscrições do usuário (e o access está revogado: o DELETE levaria 401).
    if (!servidorJaEncerrou) {
      void apagarInscricaoDoPush(accessTokenValue).catch((e: unknown) =>
        console.debug("[AuthContext] apagarInscricaoDoPush falhou:", e)
      );
    }

    // Chamar endpoint de logout se necessário
    fetch(`${API_BASE_URL}/auth/logout`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        refresh_token: refreshTokenValue,
      }),
    }).catch(console.error);
  }, []);

  const logout = useCallback((): void => encerrarSessaoLocal(false), [encerrarSessaoLocal]);

  // Delega à renovação única da aba/abas (`renovarSessao`), que guarda o PAR novo: o refresh antigo é revogado a cada giro. O contrato
  // segue `Promise<boolean>`: `false` é "não renovou", e só o `"recusou"` (servidor negou) derruba a pessoa.
  const refreshToken = async (): Promise<boolean> => {
    const resultado = await renovarSessao();
    if (resultado === "renovou") return true;
    if (resultado === "recusou") logout();
    return false;
  };

  // Sai do sistema em TODOS os aparelhos. Só desloga aqui quando o servidor confirmou (204): com erro a pessoa continua logada e
  // quem chamou mostra a mensagem. Usa `fetch` com o access atual, como o `logout`, para não passar pelos interceptadores do axios.
  // Access vencido (computador que dormiu, timer atrasado) devolve 401: lançar deixaria logada quem acabou de pedir para sair, então
  // renova UMA vez e repete UMA vez.
  const sairDeTodosOsAparelhos = async (): Promise<void> => {
    const pedir = async (): Promise<Response> => {
      try {
        return await fetch(`${API_BASE_URL}/auth/sair-de-todos`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getStoredAuthData()?.access_token || ""}`,
            "Content-Type": "application/json",
          },
        });
      } catch {
        throw new Error("Não foi possível conectar ao servidor. Tente novamente em instantes.");
      }
    };

    let resposta = await pedir();

    if (resposta.status === 401) {
      const resultado = await renovarSessao();
      if (resultado === "recusou") {
        // A sessão já acabou no servidor: o que a pessoa pediu (sair) está cumprido neste aparelho, então não é erro.
        logout();
        return;
      }
      if (resultado === "tentar_depois") {
        throw new Error("Sem conexão com o servidor agora. Tente de novo.");
      }
      resposta = await pedir(); // o access novo já está guardado; a 2ª resposta, qualquer que seja, vale como está
    }

    if (resposta.status !== 204) {
      let detalhe: unknown;
      try {
        detalhe = ((await resposta.json()) as { detail?: unknown } | null)?.detail;
      } catch {
        // corpo que não é JSON (ex.: "no available server" do proxy): cai na frase genérica
      }
      throw new Error(
        typeof detalhe === "string" && detalhe !== ""
          ? detalhe
          : "Não foi possível sair de todos os aparelhos agora. Tente de novo."
      );
    }

    // 204: o servidor revogou o access e apagou o push. Só a saída local, sem pedido com o access morto.
    encerrarSessaoLocal(true);
  };

  const getToken = (): string | null => {
    const authData = getStoredAuthData();
    return authData?.access_token || null;
  };

  // Limpa o flag de troca obrigatória após uma troca bem-sucedida, no estado e
  // no storage, pra o ProtectedRoute parar de redirecionar pra tela de troca.
  const clearPasswordChangeRequired = (): void => {
    const authData = getStoredAuthData();
    if (authData) {
      const updated: AuthStorage = {
        ...authData,
        user: { ...authData.user, password_change_required: false },
      };
      setStoredAuthData(updated);
      setUser(updated.user);
    } else {
      setUser((prev) => (prev ? { ...prev, password_change_required: false } : prev));
    }
  };

  // Renovação automática: com a pessoa logada, renova o access ANTES de ele vencer (`ANTECEDENCIA_DA_RENOVACAO_MS` de folga). Sem isso a
  // primeira requisição depois do vencimento levava 401 e a sessão dependia do interceptador do axios. Tudo é limpo quando a pessoa sai.
  const logado = user !== null;
  useEffect(() => {
    if (!logado) return;

    let encerrado = false;
    let renovando = false;
    let relogio: ReturnType<typeof setTimeout> | undefined;

    /** Falta `ANTECEDENCIA_DA_RENOVACAO_MS` ou menos para o access vencer (inclusive o 0 exato do timer). Sem prazo guardado, não há o que contar. */
    const venceEmBreve = (): boolean => {
      const expiraEm = getStoredAuthData()?.expires_at;
      return typeof expiraEm === "number" && expiraEm - Date.now() <= ANTECEDENCIA_DA_RENOVACAO_MS;
    };

    /** (Re)arma o timer pelo `expires_at` guardado: `expires_at − 5 min` (mínimo 0), nunca antes da espera de uma falha recente. */
    const agendar = () => {
      clearTimeout(relogio);
      const expiraEm = getStoredAuthData()?.expires_at;
      if (typeof expiraEm !== "number") return;
      const espera = Math.max(
        0,
        expiraEm - ANTECEDENCIA_DA_RENOVACAO_MS - Date.now(),
        esperarAte.current - Date.now()
      );
      relogio = setTimeout(disparar, Math.min(espera, TETO_DO_TIMER_MS));
    };

    // O timer só renova se for mesmo a hora: com a espera acima do teto do `setTimeout`, ou um prazo que mudou, ele apenas reagenda.
    const disparar = () => {
      if (venceEmBreve()) void renovar();
      else agendar();
    };

    const renovar = async () => {
      if (renovando) return; // timer, aba que voltou e rede que voltou podem coincidir: a renovação em curso já resolve
      renovando = true;
      try {
        const resultado = await renovarSessao();
        if (encerrado) return; // a pessoa saiu (ou o provider desmontou) enquanto o pedido andava
        if (resultado === "recusou") {
          logout();
          return;
        }
        // "tentar_depois" NÃO derruba ninguém: tenta de novo em `ESPERA_DEPOIS_DE_FALHA_MS`, ainda dentro da janela de 120 s em que o
        // backend aceita o refresh velho. "renovou": reagenda pelo `expires_at` novo.
        if (resultado === "tentar_depois")
          esperarAte.current = Date.now() + ESPERA_DEPOIS_DE_FALHA_MS;
        agendar();
      } finally {
        renovando = false;
      }
    };

    // Aba que volta a ficar visível (o computador pode ter dormido e os timers atrasado) e rede que volta: renova já se estiver na folga.
    const aoFicarVisivel = () => {
      if (document.visibilityState === "visible" && venceEmBreve()) void renovar();
    };
    const aoVoltarARede = () => {
      if (venceEmBreve()) void renovar();
    };

    // Outra aba mexeu na sessão. Apagada (o "Sair" de lá, ou `localStorage.clear()` com a chave nula): sai daqui SEM chamar a API nem
    // `executarAntesDeSair` — a outra aba já fez o logout de verdade. Trocada (outra aba renovou ou entrou de novo): só reagenda.
    const aoMudarOArmazenamento = (evento: StorageEvent) => {
      if (evento.key !== null && evento.key !== AUTH_STORAGE_KEY) return;
      if (evento.key === null || evento.newValue === null) setUser(null);
      else agendar();
    };

    document.addEventListener("visibilitychange", aoFicarVisivel);
    window.addEventListener("online", aoVoltarARede);
    window.addEventListener("storage", aoMudarOArmazenamento);
    agendar();

    return () => {
      encerrado = true;
      clearTimeout(relogio);
      document.removeEventListener("visibilitychange", aoFicarVisivel);
      window.removeEventListener("online", aoVoltarARede);
      window.removeEventListener("storage", aoMudarOArmazenamento);
    };
  }, [logado, logout]);

  const value: AuthContextType = {
    user,
    login,
    logout,
    refreshToken,
    sairDeTodosOsAparelhos,
    getToken,
    clearPasswordChangeRequired,
    isAuthenticated: !!user,
    isLoading,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
